import React, { useEffect, useState } from "react";
import {
  Spin,
  Table,
  Button,
  InputNumber,
  Space,
  Popconfirm,
  Row,
  Select,
} from "antd";
import { ExpenseServices } from "../../Services/Manage/ExpenseServices";
import { PlusOutlined, DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import { UserService } from "../../Services/Manage/UserService";
import useMessage from "antd/es/message/useMessage";

const PoPayments = ({ getAllExpense, quotationNo, grandTotal }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [message, msgContex] = useMessage();

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        if (!quotationNo) {
          setRows([]);
          setIsEditMode(false);
          return;
        }

        setLoading(true);
        const response = await ExpenseServices.getPaymentByPoId(quotationNo);

        const payments = (response?.data || []).map((p) => ({
          ...p,
          key: p.id || Date.now(),
          paid_amt: Number(p.paid_amt) || 0,
          pay_mode: p.pay_mode || "",
        }));

        if (payments.length > 0) {
          setIsEditMode(true);
        }

        setRows(payments);
      } catch (error) {
        console.error("Error fetching payments:", error);
      } finally {
        setLoading(false);
      }
    };

    if (quotationNo) fetchPayments();
  }, [quotationNo]);

  const getRunningTotal = () =>
    rows.reduce((sum, row) => sum + (Number(row.paid_amt) || 0), 0);

  const handleAddRow = () => {
    const runningTotal = getRunningTotal();
    if (runningTotal >= grandTotal) {
      message.warning("Grand total already fully paid!");
      return;
    }

    setRows([
      ...rows,
      {
        key: Date.now(),
        paid_amt: 0,
        pay_mode: "",
      },
    ]);
  };

  const handleDelete = (key) => {
    setRows(rows.filter((row) => row.key !== key));
  };

  const handleChange = (value, key) => {
    const runningTotal = getRunningTotal();
    const currentRow = rows.find((r) => r.key === key);

    const balanceBefore =
      grandTotal - (runningTotal - (currentRow?.paid_amt || 0));

    if (value < 0) {
      message.warning("Installment cannot be negative!");
      return;
    }
    if (value > balanceBefore) {
      message.warning(
        `Installment cannot exceed remaining balance (${balanceBefore})`
      );
      return;
    }

    setRows(
      rows.map((row) =>
        row.key === key ? { ...row, paid_amt: Number(value) || 0 } : row
      )
    );
  };

  const handlePayModeChange = (value, key) => {
    setRows(
      rows.map((row) =>
        row.key === key ? { ...row, pay_mode: value } : row
      )
    );
  };

  const handleSave = async () => {
    try {
      if (rows.length === 0) {
        message.warning("No payments to save!");
        return;
      }
      const user = await UserService.getOmeUser();

      let runningTotal = 0;
      const result = rows.map((row) => {
        runningTotal += row.paid_amt;
        const balance = Math.max(grandTotal - runningTotal, 0);

        return {
          id: row.id,
          added_by: user.uid,
          po_no: quotationNo,
          paid_amt: row.paid_amt,
          balance,
          status: balance === 0 ? "Paid" : "Pending",
          grand_total: grandTotal,
          pay_mode: row.pay_mode, 
         };
      });

      let request;
      if (isEditMode) {
        request = await ExpenseServices.updatePoPayment(result);
        getAllExpense();
      } else {
        request = await ExpenseServices.createPoPayment(result);
        getAllExpense();
      }

      if (request?.status) {
        message.success(request.message || "Payments saved successfully!");
        getAllExpense();
      } else {
        message.error(request?.message || "Failed to save payments");
        getAllExpense();
      }
    } catch (error) {
      console.log(error);
      message.error("Something went wrong while saving payments");
      getAllExpense();
    }
  };

  const columns = [
    {
      title: "Installment Amount",
      dataIndex: "paid_amt",
      key: "paid_amt",
      render: (text, record) => {
        const runningTotalExcludingCurrent = rows.reduce(
          (sum, r) =>
            sum + (r.key === record.key ? 0 : (Number(r.paid_amt) || 0)),
          0
        );
        const maxBalance = Math.max(
          grandTotal - runningTotalExcludingCurrent,
          0
        );

        return (
          <InputNumber
            size="large"
            min={0}
            max={maxBalance}
            style={{ width: "100%" }}
            value={record.paid_amt}
            onChange={(value) => handleChange(value, record.key)}
          />
        );
      },
    },

    // --------------------------
    // FIXED PAY MODE COLUMN
    // --------------------------
    {
      title: "Pay Mode",
      key: "pay_mode",
      render: (_, record) => (
        <Select
        size="large"
          value={record.pay_mode}
          onChange={(val) => handlePayModeChange(val, record.key)}
          placeholder="Select Pay Mode"
          style={{ width: "100%" }}
          options={[
            { label: "Cash", value: "cash" },
            { label: "Card Payment", value: "card" },
            { label: "Bank Transfer", value: "bank" },
            { label: "Online Payment", value: "onlinePay" },
          ]}
        />
      ),
    },

    {
      title: "Balance",
      key: "balance",
      render: (_, __, index) => {
        let runningTotal = rows
          .slice(0, index + 1)
          .reduce((sum, r) => sum + (Number(r.paid_amt) || 0), 0);
        return Math.max(grandTotal - runningTotal, 0);
      },
    },

    // {
    //   title: "Action",
    //   key: "action",
    //   render: (_, record) => (
    //     <Popconfirm
    //       title="Remove this installment?"
    //       onConfirm={() => handleDelete(record.key)}
    //     >
    //       <Button danger icon={<DeleteOutlined />} />
    //     </Popconfirm>
    //   ),
    // },
  ];

  if (loading) return <Spin />;

  return (
    <div>
      {msgContex}

      <Row style={{ justifyContent: "space-between" }}>
        <h3>
          Grand Total: {grandTotal} | Paid: {getRunningTotal()} | Remaining:{" "}
          {grandTotal - getRunningTotal()}
        </h3>

        <Space style={{ marginBottom: 16 }}>
          <Button
            size="large"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddRow}
          >
            Add Installment
          </Button>

          <Button
            size="large"
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
          >
            {isEditMode ? "Update" : "Save"}
          </Button>
        </Space>
      </Row>

      <Table
        rowKey="key"
        dataSource={rows}
        columns={columns}
        pagination={false}
        size="small"
      />
    </div>
  );
};

export default PoPayments;
